'use client';
import React, { useState } from 'react';
import { CalendarOff, Trash2, Plus } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';

interface BlockedDate {
  id: string;
  date: string;
  reason: string;
  allDay: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function BlockDatesModal({ isOpen, onClose }: Props) {
  const [blocked, setBlocked] = useState<BlockedDate[]>([]);
  const [newDate, setNewDate] = useState('');
  const [newReason, setNewReason] = useState('');

  const handleAdd = () => {
    if (!newDate) { toast.error('Please enter a date to block'); return; }
    const newEntry: BlockedDate = {
      id: `block-${Date.now()}`,
      date: newDate,
      reason: newReason || 'Unavailable',
      allDay: true,
    };
    setBlocked((prev) => [...prev, newEntry]);
    setNewDate('');
    setNewReason('');
    toast.success(`${newDate} blocked successfully`);
  };

  const handleRemove = (id: string) => {
    setBlocked((prev) => prev.filter((b) => b.id !== id));
    toast.success('Date unblocked');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Block Dates" size="md">
      <div className="space-y-5">
        {/* Add new */}
        <div className="bg-background border border-border rounded-xl p-4 space-y-3">
          <p className="text-xs font-semibold text-foreground">Block a New Date</p>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5" htmlFor="block-date">
              Date or Date Range
            </label>
            <input
              id="block-date"
              type="text"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              placeholder="e.g. May 15, 2026 or May 20–22, 2026"
              className="w-full px-3 py-2.5 rounded-lg bg-card border border-input text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5" htmlFor="block-reason">
              Reason (optional)
            </label>
            <input
              id="block-reason"
              type="text"
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              placeholder="Vacation, maintenance, personal..."
              className="w-full px-3 py-2.5 rounded-lg bg-card border border-input text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2 rounded-lg gold-gradient text-primary-foreground text-xs font-bold transition-all duration-150 hover:opacity-90 active:scale-95"
          >
            <Plus size={14} />
            Block Date
          </button>
        </div>

        {/* Existing blocked */}
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Currently Blocked ({blocked.length})
          </p>
          {blocked.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground/50">
              <CalendarOff size={24} className="mx-auto mb-2" />
              <p className="text-xs">No dates blocked</p>
            </div>
          ) : (
            <div className="space-y-2">
              {blocked.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-background border border-border"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <CalendarOff size={14} className="text-amber-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">{b.date}</p>
                      <p className="text-xs text-muted-foreground truncate">{b.reason}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemove(b.id)}
                    title="Unblock this date"
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-all duration-150 flex-shrink-0"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}